import React, { Suspense } from 'react';
import { EmDash, WrapperWidget } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { formatPrice } from '~/helper/formatPrice';

const LineChart = React.lazy(() =>
  import('~/components/charts/LineChart').then((module) => ({
    default: module.LineChart,
  })),
);

interface Props {
  readonly title: string;
  readonly infoText?: string;
  readonly barChartData?: {
    date: string;
    priceData: { totalPriceInclParking: string };
  }[];
  readonly price?: string;
  readonly evaluation?: {
    readonly estimatedLowerValue: string;
    readonly estimatedUpperValue: string;
  };
}

export function CockpitOffersDetailWidget(props: Props) {
  const { t } = useTranslate();

  const sorted = Array.isArray(props.barChartData)
    ? [...props.barChartData].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      )
    : [];

  const series = sorted.map((item) => ({
    x: item.date,
    y: Number(item.priceData.totalPriceInclParking || 0),
  }));

  const priceColor = 'red';

  const price = props.price ? parseInt(props.price) : null;

  return (
    <WrapperWidget
      title={t(props.title)}
      text={
        props.infoText ? t(props.infoText as any) || props.infoText : undefined
      }
    >
      <div className="h-full flex flex-col">
        {series?.length && series.some((s) => s.y > 0) ? (
          <div className="w-full rounded-lg border border-slate-200 dark:border-slate-700 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
            <Suspense
              fallback={
                <div className="h-32 animate-pulse bg-slate-100 rounded" />
              }
            >
              <LineChart
                datasetIdKey={'offers-line'}
                series={series.filter((s) => s.y > 0) as any}
                yFormat="currency"
              />
            </Suspense>
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500 dark:text-slate-400">
            {t('cockpit_offers_no_price_data')}
          </div>
        )}

        {price && price > 0 ? (
          <div className="flex flex-row mt-6 justify-between w-full items-baseline">
            <div className="text-sm md:text-base text-slate-600 dark:text-slate-400">
              {t('cockpit_offers_detail_widget_price')}
            </div>
            <div className="text-base md:text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              <span
                className="mr-1 font-bold"
                style={{ color: priceColor }}
              >
                {EmDash}
              </span>
              {formatPrice(price)}
            </div>
          </div>
        ) : null}
      </div>
    </WrapperWidget>
  );
}
